import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, Rss } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { StepCompany, StepGoal, StepFormat, StepGenerate, StepSave, formats } from "@/components/wizard/WizardSteps";
import StepMedia from "@/components/wizard/StepMedia";
import StepEdit from "@/components/wizard/StepEdit";
import RssImportDialog from "@/components/RssImportDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map format -> preferred crop variant key (matches CROP_PRESETS in server.py)
const getCropVariantKey = (formatType) => {
  if (!formatType) return null;
  const fmt = formats.find((f) => f.value === formatType);
  if (!fmt) return null;
  if (fmt.type === "story" || fmt.type === "reel") return "instagram_story"; // 9:16
  return "instagram_feed_4_5"; // 4:5 portrait feed for Posts (IG/FB)
};

export default function ContentCreatePage() {
  const [step, setStep] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [media, setMedia] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const [companyId, setCompanyId] = useState("");
  const [goal, setGoal] = useState("");
  const [formatType, setFormatType] = useState("");
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [generated, setGenerated] = useState(null);
  const [seasonCtx, setSeasonCtx] = useState(null);
  const [selectedContent, setSelectedContent] = useState({});
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("18:00");
  const [thumbnails, setThumbnails] = useState({});
  const [variantThumbs, setVariantThumbs] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [userBrief, setUserBrief] = useState("");
  const [rssOpen, setRssOpen] = useState(false);
  const [applyBrandKit, setApplyBrandKit] = useState(true);
  const [ctaLinkId, setCtaLinkId] = useState("auto");
  const [brandKitInfo, setBrandKitInfo] = useState(null);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [businessUnitId, setBusinessUnitId] = useState("none");
  const [connections, setConnections] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    axios.get(`${API}/social/connections`, { withCredentials: true })
      .then((r) => setConnections(r.data.items || []))
      .catch(() => setConnections([]));
  }, []);

  const connectedPlatforms = useMemo(() => {
    const set = new Set();
    connections
      .filter((c) => c.company_id === companyId && c.status === "active")
      .forEach((c) => {
        if (c.platform === "meta") { set.add("instagram"); set.add("facebook"); }
        else set.add(c.platform);
      });
    return [...set];
  }, [connections, companyId]);

  useEffect(() => {
    if (!companyId) { setBusinessUnits([]); setBusinessUnitId("none"); return; }
    axios.get(`${API}/companies/${companyId}/business-units`, { withCredentials: true })
      .then((r) => setBusinessUnits(r.data.items || []))
      .catch(() => setBusinessUnits([]));
    setBusinessUnitId("none");
  }, [companyId]);

  useEffect(() => {
    axios.get(`${API}/companies`, { withCredentials: true })
      .then((r) => setCompanies(r.data)).catch(console.error);
  }, []);

  const loadMedia = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await axios.get(`${API}/media`, { params: { company_id: companyId }, withCredentials: true });
      setMedia(res.data);
    } catch (e) { console.error(e); }
  }, [companyId]);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  // Load campaigns and templates when company changes
  useEffect(() => {
    if (!companyId) return;
    Promise.all([
      axios.get(`${API}/campaigns`, { params: { company_id: companyId }, withCredentials: true }),
      axios.get(`${API}/prompt-templates`, { params: { company_id: companyId }, withCredentials: true }),
    ]).then(([ca, te]) => {
      setCampaigns(ca.data.filter((c) => c.status === "active"));
      setTemplates(te.data.filter((t) => t.is_active));
    }).catch(console.error);
  }, [companyId]);

  // Load brand kit info (for the "apply overlay" toggle default + which platforms are enabled)
  useEffect(() => {
    if (!companyId) { setBrandKitInfo(null); return; }
    axios.get(`${API}/brand-kit/${companyId}`, { withCredentials: true })
      .then((r) => {
        setBrandKitInfo(r.data);
        setApplyBrandKit(r.data?.apply_by_default !== false);
      })
      .catch(() => setBrandKitInfo(null));
  }, [companyId]);

  // Load thumbnails
  useEffect(() => {
    media.forEach((m) => {
      if ((m.content_type?.startsWith("image/") || m.content_type?.startsWith("video/")) && !thumbnails[m.id]) {
        axios.get(`${API}/media/thumb/${m.id}`, { withCredentials: true, responseType: "blob" })
          .then((r) => setThumbnails((p) => ({ ...p, [m.id]: URL.createObjectURL(r.data) })))
          .catch(() => {});
      }
    });
  }, [media]);

  // Load variant thumbnails for current format (1:1 or 9:16)
  const currentVariantKey = getCropVariantKey(formatType);
  useEffect(() => {
    if (!currentVariantKey) return;
    media.forEach((m) => {
      const v = m.variants?.[currentVariantKey];
      if (!v) return;
      if (variantThumbs[m.id]?.[currentVariantKey]) return;
      axios.get(`${API}/media/thumb/${m.id}?variant=${currentVariantKey}`, { withCredentials: true, responseType: "blob" })
        .then((r) => {
          const url = URL.createObjectURL(r.data);
          setVariantThumbs((p) => ({
            ...p,
            [m.id]: { ...(p[m.id] || {}), [currentVariantKey]: url }
          }));
        })
        .catch(() => {});
    });
  }, [media, currentVariantKey]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/content/generate`, {
        company_id: companyId,
        goal,
        format_type: formatType,
        media_ids: selectedMedia,
        template_id: selectedTemplate && selectedTemplate !== "none" ? selectedTemplate : undefined,
        campaign_id: selectedCampaign && selectedCampaign !== "none" ? selectedCampaign : undefined,
        user_brief: userBrief?.trim() || undefined,
      }, { withCredentials: true });
      setGenerated(res.data.voice_check ? { ...res.data.generated, _voice_check: res.data.voice_check } : res.data.generated);
      setSeasonCtx(res.data.seasonal_context);
      // Pre-select first variants
      const g = res.data.generated;
      setSelectedContent({
        caption: g.captions?.[0] || g.caption || "",
        hook: g.hooks?.[0] || g.hook || "",
        cta: g.ctas?.[0] || g.closing_cta || "",
        facebook_text: g.facebook_text || "",
        hashtags: g.hashtags || [],
        scenes: g.scenes || [],
        music_direction: g.music_direction || "",
      });
      setStep(5);
      toast.success("Content generiert!");
    } catch (e) {
      toast.error("Fehler bei der Generierung");
      console.error(e);
    }
    setGenerating(false);
  };

  const handleSave = async (status = "draft") => {
    setSaving(true);
    const fmt = formats.find((f) => f.value === formatType);
    try {
      await axios.post(`${API}/content`, {
        company_id: companyId,
        type: fmt?.type || "post",
        platform: fmt?.platform || "instagram",
        goal,
        format_type: formatType,
        status,
        media_ids: selectedMedia,
        generated_content: generated,
        selected_content: selectedContent,
        schedule_date: scheduleDate || null,
        schedule_time: scheduleTime || null,
        cta_link_id: ctaLinkId === "auto" ? undefined : ctaLinkId,
        campaign_id: selectedCampaign && selectedCampaign !== "none" ? selectedCampaign : null,
        template_id: selectedTemplate && selectedTemplate !== "none" ? selectedTemplate : null,
        apply_brand_kit: applyBrandKit,
        business_unit_id: businessUnitId !== "none" ? businessUnitId : null,
      }, { withCredentials: true });
      toast.success(status === "review" ? "Zur Pruefung eingereicht" : "Als Entwurf gespeichert");
      // Reset
      setStep(0);
      setCompanyId("");
      setGoal("");
      setFormatType("");
      setSelectedMedia([]);
      setGenerated(null);
      setSelectedContent({});
      setScheduleDate("");
      setUserBrief("");
    } catch (e) {
      toast.error("Fehler beim Speichern");
    }
    setSaving(false);
  };

  const toggleMedia = (id) => {
    setSelectedMedia((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const stepLabels = [t("content.step_company"), t("content.step_goal"), t("content.step_format"), t("content.step_media"), t("content.step_generate"), t("content.step_edit"), t("content.step_save")];

  const canNext = () => {
    if (step === 0) return !!companyId;
    if (step === 1) return !!goal;
    if (step === 2) return !!formatType;
    if (step === 3) return selectedMedia.length > 0;
    return true;
  };

  return (
    <div data-testid="content-create-page" className="space-y-6 max-w-4xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight">{t("content.title")}</h1>
          <p className="text-[var(--studio-text-muted)] mt-1">{t("content.subtitle")}</p>
          {companyId && (
            <Badge className="mt-2 bg-blue-100 text-blue-800 border-blue-200" data-testid="wizard-company-badge">
              Firma: {companies.find((c) => c.id === companyId)?.name || ""}
            </Badge>
          )}
        </div>
        <Button
          variant="outline"
          onClick={() => setRssOpen(true)}
          data-testid="open-rss-import-btn"
          className="whitespace-nowrap"
        >
          <Rss className="w-4 h-4 mr-1 text-[#2563EB]" /> RSS-Import
        </Button>
      </div>

      {/* Step indicators */}
      <div className="step-indicator" data-testid="step-indicator">
        {stepLabels.map((label, i) => (
          <div key={i} className="flex items-center gap-1">
            <div className={`step-dot ${i === step ? "active" : i < step ? "completed" : ""}`} />
            {i < stepLabels.length - 1 && <div className="w-2" />}
          </div>
        ))}
      </div>
      <p className="text-sm text-[var(--studio-text-muted)]">
        Schritt {step + 1}: <span className="font-medium text-[var(--studio-text)]">{stepLabels[step]}</span>
      </p>

      {/* Step 0: Company */}
      {step === 0 && <StepCompany companies={companies} companyId={companyId} setCompanyId={setCompanyId} t={t} />}

      {/* Step 1: Goal */}
      {step === 1 && <StepGoal goal={goal} setGoal={setGoal} t={t} />}

      {/* Step 2: Format */}
      {step === 2 && <StepFormat
        formatType={formatType}
        setFormatType={setFormatType}
        t={t}
        activePlatforms={connectedPlatforms.length > 0 ? connectedPlatforms : (companies.find((c) => c.id === companyId)?.platforms || [])}
        connectionsOnly={connectedPlatforms.length > 0}
      />}

      {/* Step 3: Media */}
      {step === 3 && (
        <StepMedia
          media={media}
          selectedMedia={selectedMedia}
          toggleMedia={toggleMedia}
          thumbnails={thumbnails}
          variantThumbs={variantThumbs}
          currentVariantKey={currentVariantKey}
          companyId={companyId}
          onMediaReload={loadMedia}
          platform={(formats.find((f) => f.value === formatType)?.platform) || "instagram"}
          t={t}
        />
      )}

      {/* Step 4: Generate */}
      {step === 4 && (
        <StepGenerate
          companies={companies}
          companyId={companyId}
          goal={goal}
          formatType={formatType}
          selectedMedia={selectedMedia}
          templates={templates}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          campaigns={campaigns}
          selectedCampaign={selectedCampaign}
          setSelectedCampaign={setSelectedCampaign}
          userBrief={userBrief}
          setUserBrief={setUserBrief}
          generating={generating}
          onGenerate={handleGenerate}
          t={t}
        />
      )}

      {/* Step 5: Review/Edit */}
      {step === 5 && generated && (
        <StepEdit
          generated={generated}
          seasonCtx={seasonCtx}
          selectedContent={selectedContent}
          setSelectedContent={setSelectedContent}
          companyId={companyId}
          formatType={formatType}
          companies={companies}
          formats={formats}
          selectedMedia={selectedMedia}
          media={media}
          thumbnails={thumbnails}
          variantThumbs={variantThumbs}
          currentVariantKey={currentVariantKey}
        />
      )}

      {/* Step 6: Save */}
      {step === 6 && (
        <StepSave
          scheduleDate={scheduleDate}
          setScheduleDate={setScheduleDate}
          scheduleTime={scheduleTime}
          setScheduleTime={setScheduleTime}
          saving={saving}
          onSave={handleSave}
          companyId={companyId}
          formatType={formatType}
          applyBrandKit={applyBrandKit}
          setApplyBrandKit={setApplyBrandKit}
          brandKitInfo={brandKitInfo}
          selectedMedia={selectedMedia}
          hasVideoSelected={selectedMedia.some((id) => media.find((m) => m.id === id)?.content_type?.startsWith("video/"))}
          businessUnits={businessUnits}
          businessUnitId={businessUnitId}
          setBusinessUnitId={setBusinessUnitId}
          ctaLinkId={ctaLinkId}
          setCtaLinkId={setCtaLinkId}
          t={t}
        />
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-2 items-center gap-3">
        <Button
          variant="outline"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          data-testid="prev-step-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> {t("content.back")}
        </Button>
        {step === 3 && selectedMedia.length === 0 && (
          <p className="text-xs text-amber-600 text-right flex-1" data-testid="media-required-hint">
            Bitte waehle mindestens ein Medium aus oder erstelle zuerst ein Bild bzw. Video.
          </p>
        )}
        {step < 4 && (
          <Button
            onClick={() => setStep((s) => s + 1)}
            disabled={!canNext()}
            data-testid="next-step-btn"
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
          >
            {t("content.next")} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
        {step === 5 && (
          <Button
            onClick={() => setStep(6)}
            data-testid="go-to-save-btn"
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
          >
            Weiter zum Speichern <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>

      <RssImportDialog
        companies={companies}
        open={rssOpen}
        onOpenChange={setRssOpen}
        onImported={() => { /* no-op: imported content appears in Approval/Calendar */ }}
      />
    </div>
  );
}
